import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useNavigate } from 'react-router-dom';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import { Flame, Trophy, Users, CalendarDays, BookOpen, MessageSquare, Heart } from 'lucide-react';

export default function HerDashboardPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [clients, setClients] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [enrollForm, setEnrollForm] = useState({ client_id: '', assigned_specialist_id: '', referred_by_referral_source_id: '' });

  const canEnroll = ['admin', 'coordinator'].includes(user.role);
  const isFacilitator = user.role === 'facilitator';
  const isPeer = user.role === 'peer_specialist';
  const isCoordinator = user.role === 'coordinator';
  const isExecutive = user.role === 'admin';
  const isParticipant = user.role === 'client';

  useEffect(() => {
    api.get('/her/modules').then(r => setModules(r.data)).catch(() => {});
    api.get('/her/completions').then(r => setCompletions(r.data)).catch(() => {});
    api.get('/her/sessions').then(r => setSessions(r.data)).catch(() => {});
    api.get('/her/facilitators').then(r => setFacilitators(r.data)).catch(() => {});
    if (canEnroll) {
      api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
      api.get('/users?role=peer_specialist').then(r => setSpecialists(r.data)).catch(() => {});
      api.get('/users?role=referral_source').then(r => setReferralSources(r.data)).catch(() => {});
    }
  }, []);

  const completedCount = completions.length;
  const totalModules = modules.length;
  const progress = totalModules ? Math.round((completedCount / totalModules) * 100) : 0;
  const pendingApprovals = completions.filter(c => c.approval_status === 'pending');

  const handleSupportRequest = async () => {
    if (!supportMessage.trim()) return toast.error('Please share your question');
    try {
      await api.post('/her/escalate', { content: supportMessage.trim() });
      toast.success('Request sent to coordinator');
      setSupportMessage('');
      setSupportOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to send request');
    }
  };

  const handleEnroll = async () => {
    if (!enrollForm.client_id) return toast.error('Select a participant');
    try {
      await api.post('/her/enroll', {
        client_id: enrollForm.client_id,
        assigned_specialist_id: enrollForm.assigned_specialist_id || undefined,
        referred_by_referral_source_id: enrollForm.referred_by_referral_source_id || undefined
      });
      toast.success('Participant enrolled in HER');
      setEnrollForm({ client_id: '', assigned_specialist_id: '', referred_by_referral_source_id: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to enroll participant');
    }
  };

  return (
    <div className="space-y-6" data-testid="her-dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
            {isFacilitator ? 'HER Facilitator Hub' : isPeer ? 'HER Peer Support Hub' : 'HER Program Hub'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isFacilitator
              ? 'Deliver curriculum, track attendance, and document module progress.'
              : isPeer
                ? 'Support participants, review module submissions, and coordinate with facilitators.'
                : 'HER curriculum milestones, attendance tracking, and facilitator guidance.'}
          </p>
        </div>
        <Button onClick={() => navigate('/her/modules')} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full gap-2" data-testid="her-go-modules">
          <Flame size={16} /> View Modules
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="her-stat-modules" onClick={() => navigate('/her/modules')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center mb-2"><BookOpen className="w-4 h-4 text-orange-600" /></div>
            <p className="text-2xl font-bold">{totalModules}</p>
            <p className="text-xs text-muted-foreground">HER Modules</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="her-stat-completions" onClick={() => navigate('/her/modules')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center mb-2"><Trophy className="w-4 h-4 text-emerald-600" /></div>
            <p className="text-2xl font-bold">{completedCount}</p>
            <p className="text-xs text-muted-foreground">Completions</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="her-stat-sessions" onClick={() => navigate('/her/attendance')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center mb-2"><CalendarDays className="w-4 h-4 text-sky-600" /></div>
            <p className="text-2xl font-bold">{sessions.length}</p>
            <p className="text-xs text-muted-foreground">Attendance Sessions</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer" data-testid="her-stat-facilitators" onClick={() => navigate('/her/facilitators')}>
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center mb-2"><Users className="w-4 h-4 text-indigo-600" /></div>
            <p className="text-2xl font-bold">{facilitators.length}</p>
            <p className="text-xs text-muted-foreground">Facilitators</p>
          </CardContent>
        </Card>
      </div>

      {canEnroll && (isFacilitator || isPeer || isCoordinator || isExecutive) && (
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-approvals-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Pending Module Approvals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingApprovals.slice(0, 4).map(item => (
              <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`her-approval-${item.id}`}>
                <span>{item.module_name}</span>
                <Badge variant="secondary" className="bg-amber-50 text-amber-700">Pending</Badge>
              </div>
            ))}
            {pendingApprovals.length === 0 && (
              <p className="text-sm text-muted-foreground">No approvals waiting.</p>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/her/modules')} data-testid="her-approval-view">
              Review Modules
            </Button>
          </CardContent>
        </Card>
      )}

      {canEnroll && (
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-enroll-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Enroll Participant in HER</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-3">
            <Select value={enrollForm.client_id} onValueChange={value => setEnrollForm(prev => ({ ...prev, client_id: value }))}>
              <SelectTrigger data-testid="her-enroll-client"><SelectValue placeholder="Participant" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={enrollForm.assigned_specialist_id} onValueChange={value => setEnrollForm(prev => ({ ...prev, assigned_specialist_id: value }))}>
              <SelectTrigger data-testid="her-enroll-specialist"><SelectValue placeholder="Assign Specialist (optional)" /></SelectTrigger>
              <SelectContent>
                {specialists.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={enrollForm.referred_by_referral_source_id} onValueChange={value => setEnrollForm(prev => ({ ...prev, referred_by_referral_source_id: value }))}>
              <SelectTrigger data-testid="her-enroll-referral"><SelectValue placeholder="Referral Source (optional)" /></SelectTrigger>
              <SelectContent>
                {referralSources.map(r => (
                  <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-full md:col-span-3" onClick={handleEnroll} data-testid="her-enroll-submit">
              Enroll Participant
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-rose-50 to-pink-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-600" /> HER Progress Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-rose-900">
            {totalModules === 0
              ? 'Modules are loading. Your HER milestones will appear here once the program starts.'
              : `You have completed ${completedCount} of ${totalModules} modules (${progress}%). Keep building consistency.`}
          </p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-quick-links">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">HER Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/her/modules')} data-testid="her-link-modules">
              Modules
            </Button>
            <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/her/attendance')} data-testid="her-link-attendance">
              Attendance
            </Button>
            {(isFacilitator || isPeer || isCoordinator || isExecutive) && (
              <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/her/messages')} data-testid="her-link-messages">
                HER Messages
              </Button>
            )}
            {(isPeer || isCoordinator || isExecutive) && (
              <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/her/facilitators')} data-testid="her-link-facilitators">
                Facilitators
              </Button>
            )}
            {user.role === 'referral_source' && (
              <Button variant="outline" className="h-auto py-3 rounded-xl" onClick={() => navigate('/her/referral-portal')} data-testid="her-link-referral">
                Referral Portal
              </Button>
            )}
          </CardContent>
        </Card>

        {user.role === 'client' && (
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-support-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-teal-600" /> Request Coordinator Support
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">Use this when you need program-level guidance beyond your peer specialist.</p>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" onClick={() => setSupportOpen(true)} data-testid="her-support-open">
                Request Support
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Request Coordinator Support</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <DictationInput
              value={supportMessage}
              onChange={(cleaned) => setSupportMessage(cleaned)}
              rows={4}
              placeholder="Share what you need help with..."
              promptContext={{
                context: 'her_support',
                taskName: 'Request Coordinator Support',
                taskDescription: 'Program escalation request',
                category: 'support'
              }}
              data-testid="her-support-input"
            />
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" onClick={handleSupportRequest} data-testid="her-support-submit">
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
